import { useMemo, useRef, useState, useEffect } from "react";

/**
 * StandardTable — tableaux larges UiKit.
 *
 * Colonnes :
 * - fluidColCount : colonnes de donnees fluides
 * - fixedColWidth : largeur fixe de la derniere colonne SI bouton/menu actions (0 = toutes fluides)
 *
 * Formules (FLUID_MIN_PX=300, FLUID_STANDARD_PX=450) :
 * - largeurMinimum  = fluidColCount × 300 + fixedColWidth
 * - largeurReference = fluidColCount × 450 + fixedColWidth (grille construction UiKit)
 *
 * Zones (tous StandardTable, via clamp sur .table-standard-inner) :
 * - parent < largeurMinimum  : inner = largeurMinimum, scroll horizontal
 * - min <= parent <= ref     : inner 100%
 * - parent > largeurReference: inner = largeurReference, centre
 *
 * Voir .cursor/rules/frontend-tables-standard.mdc et src/ui/ui-guidelines.md
 */
const FLUID_STANDARD_PX = 450;
const FLUID_MIN_PX = 300;

const ALIGN_TO_FLEX = {
  left: "flex-start",
  center: "center",
  right: "flex-end",
};

const measureConstructionWidths = (scrollEl, innerEl) => {
  if (!scrollEl || !innerEl) return { total: 0, columns: [] };
  const total = Math.round(scrollEl.clientWidth);
  const header = innerEl.querySelector(".table-standard-header");
  if (!header) return { total, columns: [] };
  const cells = header.querySelectorAll(".table-standard-header-cell");
  const columns = Array.from(cells).map((cell) => Math.round(cell.offsetWidth));
  return { total, columns };
};

const StandardTable = ({
  fluidColCount = 4,
  fixedColWidth = 150,
  columnAlignments,
  containerClassName,
  /** No horizontal scroll: inner uses full width, min width not forced by column count */
  fitContainer = false,
  showConstructionGrid = false,
  showConstructionWidthLabels = true,
  topBar,
  header,
  children,
  bottom,
}) => {
  const scrollRef = useRef(null);
  const innerRef = useRef(null);
  const [constructionWidths, setConstructionWidths] = useState({
    total: 0,
    columns: [],
  });

  const containerStyle = useMemo(() => {
    const standardPx = fluidColCount * FLUID_STANDARD_PX + fixedColWidth;
    const minPx = fluidColCount * FLUID_MIN_PX + fixedColWidth;
    const style = {
      "--table-standard-width": fitContainer ? "100%" : `${standardPx}px`,
      "--table-min-width": fitContainer ? "0px" : `${minPx}px`,
      "--table-fluid-cols": fluidColCount,
      "--table-fixed-width": `${fixedColWidth}px`,
    };
    if (Array.isArray(columnAlignments) && columnAlignments.length > 0) {
      columnAlignments.forEach((align, index) => {
        if (align && ALIGN_TO_FLEX[align]) {
          style[`--table-col-align-${index}`] = ALIGN_TO_FLEX[align];
        }
      });
    }
    return style;
  }, [fluidColCount, fixedColWidth, columnAlignments, fitContainer]);

  useEffect(() => {
    if (!showConstructionGrid || !scrollRef.current || !innerRef.current)
      return;
    const scrollEl = scrollRef.current;
    const innerEl = innerRef.current;
    const update = () =>
      setConstructionWidths(measureConstructionWidths(scrollEl, innerEl));
    update();
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(scrollEl);
    return () => resizeObserver.disconnect();
  }, [showConstructionGrid, header, children]);

  const innerClass = [
    "table-standard-inner",
    showConstructionGrid && "table-standard-inner--show-construction-grid",
  ]
    .filter(Boolean)
    .join(" ");

  /** 0-width fixed column must not add an extra grid track (use explicit check: !-"0" would be wrong) */
  const fluidOnly = Number(fixedColWidth) === 0;

  return (
    <div
      className={[
        "table-standard-container",
        fluidOnly && "table-standard-container--fluid-only",
        fitContainer && "table-standard-container--fit",
        containerClassName,
      ]
        .filter(Boolean)
        .join(" ")}
      style={containerStyle}
    >
      <div className="table-standard-scroll" ref={scrollRef}>
        <div className={innerClass} ref={innerRef}>
          {showConstructionGrid &&
            showConstructionWidthLabels &&
            constructionWidths.total > 0 && (
              <div className="table-standard-construction-total">
                Largeur totale : {constructionWidths.total}px
              </div>
            )}
          {topBar}
          {header}
          {showConstructionGrid ? (
            <div className="table-standard-construction-rows-wrapper">
              {children}
              {showConstructionWidthLabels &&
                constructionWidths.columns.length > 0 && (
                  <div className="table-standard-construction-columns">
                    {constructionWidths.columns.map((width, index) => (
                      <div
                        key={index}
                        className="table-standard-construction-column-cell"
                      >
                        largeur : {width}px
                      </div>
                    ))}
                  </div>
                )}
            </div>
          ) : (
            children
          )}
          {bottom}
        </div>
      </div>
    </div>
  );
};

export default StandardTable;
